import { useEffect, useState, type ReactNode, type ComponentType, type CSSProperties } from 'react';
import { Link } from '@tanstack/react-router';
import { BRAND, STRINGS } from '@maintenance-tracker/shared';
import { cn } from '@/lib/utils';
import { captureReferralParams } from '@/lib/referral';
import {
  Check,
  ShieldCheck,
  FileSearch,
  Wrench,
  Trophy,
  Send,
  Sparkles,
  ArrowRight,
  AlertTriangle,
  Star,
  Bell,
  Clock,
  MessageSquare,
  Phone,
  Quote,
  Image,
  Search,
  Globe,
  ClipboardList,
  BadgeCheck,
} from 'lucide-react';

const faqs = [
  {
    q: 'What does it cost?',
    a: 'Contractor Growth Kit is free while we launch. No credit card. No paywall on the core kits.',
  },
  {
    q: 'Do I need to change my website?',
    a: 'No rebuild required. We only read your public pages. You choose what to copy onto the site you already have.',
  },
  {
    q: 'Do you sell my data?',
    a: 'No. We do not sell your business or customer data. See our Privacy Policy for what we collect and why.',
  },
  {
    q: 'Will you change anything on my site?',
    a: 'Never automatically. Nothing is published to your website without you copying it yourself.',
  },
  {
    q: 'What kits are included?',
    a: 'Website Growth Review, Improvement Kit (copy-ready drafts), Quote Request Kit, and Reviews & Project Proof — plus Social early access when you want it.',
  },
  {
    q: 'How long does a review take?',
    a: 'About two minutes. We only read public pages. Findings show Needs attention, Looks good, or Could not check — never invented traffic numbers.',
  },
];

const gridFeatures = [
  {
    icon: ClipboardList,
    title: 'Copy-ready fixes',
    desc: 'Drafts for service pages, contact sections, and technical SEO you can paste yourself — no developer required.',
  },
  {
    icon: Quote,
    title: 'Quote page that travels',
    desc: 'Share on your site, in texts, on a QR code, or your truck. Requests land in your inbox.',
  },
  {
    icon: BadgeCheck,
    title: 'Honest review asks',
    desc: 'Neutral messages that follow Google and FTC rules — no 5-star asks or “only if you’re happy” wording.',
  },
  {
    icon: ShieldCheck,
    title: 'Your data, protected',
    desc: 'We don’t sell your data. Export or delete anytime. Nothing publishes without your approval.',
  },
];

const steps = [
  {
    icon: FileSearch,
    title: 'Find',
    desc: STRINGS.landing.findFixWin.find + ' — with evidence from your public pages, not guesses.',
  },
  {
    icon: Wrench,
    title: 'Fix',
    desc: STRINGS.landing.findFixWin.fix + ' you can paste onto the site you already run.',
  },
  {
    icon: Trophy,
    title: 'Win',
    desc: STRINGS.landing.findFixWin.win + ' — clearer requests and real project proof.',
  },
];

const metrics = [
  { value: '2 min', label: 'Typical Website Growth Review — public pages only' },
  { value: '4 kits', label: 'Review, Improvements, Quotes, and Reviews & Proof' },
  { value: '$0', label: 'Free while we launch — no credit card' },
];

export function LandingPage() {
  useEffect(() => {
    captureReferralParams();
  }, []);

  return (
    <div className="min-h-screen overflow-x-hidden bg-canvas pb-20 font-sans text-canvas-fg antialiased md:pb-0">
      <MarketingHeader />
      <Hero />
      <ToolMarquee />
      <ProblemSection />
      <HowItWorksSection />
      <FeatureTabs />
      <FeatureGrid />
      <MetricsBand />
      <FreeWhileLaunchSection />
      <FaqSection />
      <FinalCta />
      <FooterSection />
      <MobileCta />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: faqs.map((f) => ({
              '@type': 'Question',
              name: f.q,
              acceptedAnswer: { '@type': 'Answer', text: f.a },
            })),
          }),
        }}
      />
    </div>
  );
}

/* ══════════════════════════ Brand + shared bits ══════════════════════════ */

function Logomark({ className }: { className?: string }) {
  return (
    <span
      className={cn(
        'relative inline-flex items-center justify-center overflow-hidden rounded-[11px] shadow-[0_6px_18px_-6px_rgba(43,84,228,0.8)] ring-1 ring-white/15',
        className,
      )}
    >
      <span className="absolute inset-0 bg-gradient-to-br from-accent-bright via-accent to-accent-hover" />
      <span className="absolute inset-x-0 top-0 h-1/2 bg-gradient-to-b from-white/30 to-transparent" />
      <svg viewBox="0 0 28 28" fill="none" className="relative h-[58%] w-[58%] text-white">
        <path
          d="M5 19 L11.5 12.5 L15 16 L23 8"
          stroke="currentColor"
          strokeWidth="2.4"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path d="M18.5 8 H23 V12.5" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
        <circle cx="5" cy="19" r="1.5" fill="currentColor" />
      </svg>
    </span>
  );
}

type CtaProps = {
  to?: string;
  href?: string;
  search?: Record<string, string>;
  children: ReactNode;
  variant?: 'primary' | 'outline-dark' | 'outline-light';
  size?: 'md' | 'lg';
  className?: string;
};

function Cta({ to, href, search, children, variant = 'primary', size = 'lg', className }: CtaProps) {
  const classes = cn(
    'group inline-flex select-none items-center justify-center gap-2 whitespace-nowrap rounded-full font-medium transition-all duration-200',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/50 focus-visible:ring-offset-2',
    size === 'lg' ? 'h-12 px-6 text-[15px]' : 'h-10 px-4 text-sm',
    variant === 'primary' &&
      'mkt-cta bg-accent text-white shadow-[0_10px_34px_-10px_rgba(43,84,228,0.75)] hover:bg-accent-hover focus-visible:ring-offset-ink',
    variant === 'outline-dark' &&
      'border border-ink-line bg-white/[0.03] text-ink-fg backdrop-blur hover:border-ink-fg/30 hover:bg-white/[0.07] focus-visible:ring-offset-ink',
    variant === 'outline-light' &&
      'border border-canvas-line bg-canvas-raised text-canvas-fg shadow-xs hover:border-canvas-fg/25 focus-visible:ring-offset-canvas',
    className,
  );
  if (to) {
    return (
      <Link to={to} search={search as never} className={classes}>
        {children}
      </Link>
    );
  }
  return (
    <a href={href} className={classes}>
      {children}
    </a>
  );
}

function Eyebrow({ children, tone = 'light' }: { children: ReactNode; tone?: 'light' | 'dark' }) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-2 text-[12px] font-semibold uppercase tracking-[0.16em]',
        tone === 'dark' ? 'text-accent-bright' : 'text-accent',
      )}
    >
      <span className={cn('h-1.5 w-1.5 rounded-full', tone === 'dark' ? 'bg-accent-bright' : 'bg-accent')} />
      {children}
    </span>
  );
}

function SectionHeading({
  eyebrow,
  title,
  desc,
  align = 'center',
}: {
  eyebrow: string;
  title: ReactNode;
  desc?: string;
  align?: 'center' | 'left';
}) {
  return (
    <div className={cn('max-w-2xl', align === 'center' ? 'mx-auto text-center' : 'text-left')}>
      <Eyebrow>{eyebrow}</Eyebrow>
      <h2 className="mt-4 font-display text-[28px] font-semibold leading-[1.1] tracking-[-0.025em] text-canvas-fg sm:text-[32px] md:text-[42px]">
        {title}
      </h2>
      {desc && <p className="mt-4 text-[15px] leading-relaxed text-canvas-muted sm:text-[16px]">{desc}</p>}
    </div>
  );
}

/* ══════════════════════════ Header ══════════════════════════ */

function MarketingHeader() {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const light = scrolled;
  const navLink = cn(
    'hidden px-1 py-1 text-sm transition-colors md:inline-flex',
    light ? 'text-canvas-muted hover:text-canvas-fg' : 'text-ink-muted hover:text-ink-fg',
  );

  return (
    <header className="fixed inset-x-0 top-0 z-40 flex justify-center px-3 pt-3">
      <div
        className={cn(
          'flex h-14 w-full max-w-6xl items-center justify-between gap-2 rounded-2xl px-2.5 transition-all duration-300',
          light
            ? 'border border-canvas-line bg-canvas-raised/85 shadow-[0_8px_30px_-12px_rgba(11,17,32,0.2)] backdrop-blur-xl'
            : 'border border-white/10 bg-white/[0.04] backdrop-blur-md',
        )}
      >
        <Link to="/" className="flex min-w-0 items-center gap-2.5">
          <Logomark className="h-8 w-8 shrink-0" />
          <span className={cn('truncate text-[15px] font-semibold tracking-tight', light ? 'text-canvas-fg' : 'text-ink-fg')}>
            {BRAND.name}
          </span>
        </Link>
        <nav className="flex items-center gap-1 md:gap-6">
          <a href="#how" className={navLink}>
            How it works
          </a>
          <a href="#features" className={navLink}>
            Kits
          </a>
          <Link to="/sample-report" className={navLink}>
            Sample
          </Link>
          <span className={cn('mx-1 hidden h-5 w-px md:block', light ? 'bg-canvas-line' : 'bg-white/15')} />
          <Link
            to="/login"
            className={cn(
              'hidden text-sm font-medium transition-colors sm:inline-flex',
              light ? 'text-canvas-fg hover:text-accent' : 'text-ink-fg hover:text-white',
            )}
          >
            Sign in
          </Link>
          <Cta to="/signup" size="md" className="shrink-0">
            Get started free
          </Cta>
        </nav>
      </div>
    </header>
  );
}

/* ══════════════════════════ Hero ══════════════════════════ */

function Hero() {
  const [url, setUrl] = useState('');

  return (
    <section className="mkt-grain relative overflow-hidden bg-ink pt-28 pb-16 md:pt-36 md:pb-24">
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            'radial-gradient(62% 55% at 50% -10%, rgba(91,132,255,0.28), transparent 62%), radial-gradient(40% 45% at 88% 8%, rgba(123,92,255,0.16), transparent 60%)',
        }}
      />
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.35]"
        style={{
          backgroundImage:
            'linear-gradient(rgba(255,255,255,0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.05) 1px, transparent 1px)',
          backgroundSize: '56px 56px',
          maskImage: 'radial-gradient(ellipse 78% 55% at 50% 0%, black, transparent 76%)',
        }}
      />

      <div className="relative mx-auto max-w-4xl px-4 text-center">
        <h1 className="mkt-reveal font-display text-[34px] font-semibold leading-[1.05] tracking-[-0.03em] text-ink-fg sm:text-[48px] md:text-[62px]">
          See what&apos;s costing your
          <br />
          <span className="bg-gradient-to-r from-accent-bright via-[#8aa5ff] to-accent-2 bg-clip-text text-transparent">
            website customers.
          </span>
        </h1>

        <p
          className="mkt-reveal mx-auto mt-6 max-w-xl text-[16px] leading-relaxed text-ink-muted sm:text-[18px]"
          style={{ animationDelay: '0.06s' }}
        >
          {BRAND.description}
        </p>

        <form
          className="mkt-reveal mx-auto mt-9 max-w-lg"
          style={{ animationDelay: '0.12s' }}
          onSubmit={(e) => {
            e.preventDefault();
            const q = url.trim() ? `?url=${encodeURIComponent(url.trim())}` : '';
            window.location.href = `/signup${q}`;
          }}
        >
          <div className="flex flex-col gap-3 sm:flex-row sm:items-stretch">
            <input
              type="text"
              inputMode="url"
              placeholder="yourwebsite.com"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              aria-label="Website URL"
              className="h-12 flex-1 rounded-full border border-ink-line bg-white/[0.06] px-5 text-[15px] text-ink-fg placeholder:text-ink-muted backdrop-blur focus:border-accent-bright/50 focus:outline-none focus:ring-2 focus:ring-accent/40"
            />
            <button
              type="submit"
              className="mkt-cta group inline-flex h-12 shrink-0 items-center justify-center gap-2 rounded-full bg-accent px-6 text-[15px] font-medium text-white shadow-[0_10px_34px_-10px_rgba(43,84,228,0.75)] hover:bg-accent-hover"
            >
              {STRINGS.landing.ctaCheckWebsite}
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
            </button>
          </div>
          <p className="mt-3 text-[13px] text-ink-muted">
            {STRINGS.landing.reassurance} No rebuild required.
          </p>
        </form>

        <div
          className="mkt-reveal mt-6 flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-[13px] text-ink-muted"
          style={{ animationDelay: '0.18s' }}
        >
          <span className="inline-flex items-center gap-1.5">
            <Cta href="#how" variant="outline-dark" size="md" className="!h-9 !px-4 !text-[13px]">
              See how it works
            </Cta>
          </span>
          <span className="hidden h-3 w-px bg-ink-line sm:block" />
          <span>No credit card required</span>
          <span className="hidden h-3 w-px bg-ink-line sm:block" />
          <span>Free while we launch</span>
        </div>
      </div>

      <div className="mkt-reveal relative mx-auto mt-16 max-w-5xl px-4" style={{ animationDelay: '0.24s' }}>
        <div
          className="mkt-glow pointer-events-none absolute -inset-x-16 -top-12 bottom-0"
          style={{ background: 'radial-gradient(50% 60% at 50% 0%, rgba(91,132,255,0.32), transparent 70%)' }}
        />
        <div className="relative">
          <FloatChip
            className="absolute -left-3 top-24 z-20 hidden animate-[mkt-float_6s_ease-in-out_infinite] lg:flex"
            icon={AlertTriangle}
            tone="accent"
            title="3 needs attention"
            sub="Phone hard to tap on mobile"
          />
          <FloatChip
            className="absolute -right-4 top-10 z-20 hidden animate-[mkt-float_7s_ease-in-out_infinite] lg:flex"
            style={{ animationDelay: '1s' }}
            icon={Bell}
            tone="indigo"
            title="Quote page live"
            sub="Share link or QR anytime"
          />
          <MockDashboard />
        </div>
      </div>
    </section>
  );
}

/* ══════════════════════════ Mock window primitives ══════════════════════════ */

function MockChrome({ title, children, className }: { title: string; children: ReactNode; className?: string }) {
  return (
    <div
      className={cn(
        'overflow-hidden rounded-2xl border border-black/5 bg-white shadow-[0_40px_120px_-40px_rgba(11,17,32,0.55)] ring-1 ring-black/5',
        className,
      )}
    >
      <div className="flex items-center gap-2 border-b border-canvas-line bg-[#f8f9fc] px-4 py-2.5">
        <span className="h-3 w-3 rounded-full bg-[#FF5F57]" />
        <span className="h-3 w-3 rounded-full bg-[#FEBC2E]" />
        <span className="h-3 w-3 rounded-full bg-[#28C840]" />
        <div className="ml-3 flex min-w-0 items-center gap-2 rounded-md bg-white px-2.5 py-1 text-[11px] text-canvas-muted shadow-xs ring-1 ring-canvas-line">
          <span className="h-2 w-2 shrink-0 rounded-full bg-accent" />
          <span className="truncate">{title}</span>
        </div>
      </div>
      {children}
    </div>
  );
}

function FloatChip({
  icon: Icon,
  title,
  sub,
  tone,
  className,
  style,
}: {
  icon: ComponentType<{ className?: string }>;
  title: string;
  sub: string;
  tone: 'accent' | 'indigo';
  className?: string;
  style?: CSSProperties;
}) {
  return (
    <div
      style={style}
      className={cn(
        'items-center gap-2.5 rounded-xl border border-black/5 bg-white/95 px-3 py-2.5 shadow-[0_18px_40px_-16px_rgba(11,17,32,0.5)] backdrop-blur',
        className,
      )}
    >
      <span
        className={cn(
          'flex h-8 w-8 items-center justify-center rounded-lg',
          tone === 'accent' ? 'bg-accent-soft text-accent' : 'bg-[#efeaff] text-accent-2',
        )}
      >
        <Icon className="h-4 w-4" />
      </span>
      <div>
        <p className="text-[12px] font-semibold leading-tight text-canvas-fg">{title}</p>
        <p className="text-[11px] leading-tight text-canvas-muted">{sub}</p>
      </div>
    </div>
  );
}

const STATUS_PILL: Record<string, string> = {
  Attention: 'bg-[#FEE2E2] text-[#991B1B]',
  Good: 'bg-[#DCFCE7] text-[#166534]',
  Check: 'bg-[#FEF3C7] text-[#92400E]',
};

function MockDashboard() {
  const stats = [
    { label: 'Needs attention', value: '3', icon: AlertTriangle, tone: 'text-danger' },
    { label: 'Looks good', value: '8', icon: Check, tone: 'text-success' },
    { label: 'Could not check', value: '1', icon: Search, tone: 'text-warning' },
    { label: 'Quote page', value: 'Live', icon: Globe, tone: 'text-accent' },
  ];
  const rows = [
    { name: 'Mobile phone link', reason: 'Number not tappable on phones', status: 'Attention' },
    { name: 'Services on homepage', reason: 'Trade services not listed clearly', status: 'Attention' },
    { name: 'Service area near top', reason: 'City / area missing above the fold', status: 'Attention' },
    { name: 'HTTPS & title tag', reason: 'Looks good on public pages', status: 'Good' },
  ];

  return (
    <MockChrome title="Joe's Plumbing — Website Growth Review">
      <div className="flex">
        <div className="hidden w-14 shrink-0 flex-col items-center gap-1 border-r border-canvas-line bg-[#fbfcfe] py-4 sm:flex">
          <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-accent text-white">
            <FileSearch className="h-4 w-4" />
          </span>
          {[Wrench, Quote, BadgeCheck, ClipboardList].map((Icon, i) => (
            <span key={i} className="flex h-8 w-8 items-center justify-center rounded-lg text-canvas-muted hover:bg-canvas">
              <Icon className="h-4 w-4" />
            </span>
          ))}
        </div>
        <div className="min-w-0 flex-1 bg-white p-4 md:p-5">
          <div className="flex items-end justify-between gap-2">
            <div className="min-w-0">
              <p className="text-[11px] font-medium uppercase tracking-wider text-canvas-muted">Sample report</p>
              <p className="mt-1 font-display text-xl font-semibold tracking-tight text-canvas-fg sm:text-2xl md:text-3xl">
                joesplumbing.com
              </p>
            </div>
            <span className="hidden shrink-0 items-center gap-1 rounded-full bg-accent-soft px-2.5 py-1 text-[12px] font-semibold text-accent sm:inline-flex">
              ~2 min
            </span>
          </div>
          <div className="mt-4 grid grid-cols-2 gap-2.5 md:grid-cols-4">
            {stats.map((s) => (
              <div key={s.label} className="rounded-xl border border-canvas-line bg-[#fbfcfe] p-3">
                <s.icon className={cn('h-4 w-4', s.tone)} />
                <p className="mt-2 text-lg font-semibold leading-none text-canvas-fg">{s.value}</p>
                <p className="mt-1.5 text-[11px] leading-tight text-canvas-muted">{s.label}</p>
              </div>
            ))}
          </div>
          <div className="mt-4 flex items-center justify-between px-0.5">
            <p className="text-[12px] font-semibold text-canvas-fg">Findings</p>
            <span className="text-[11px] text-canvas-muted">Evidence-backed only</span>
          </div>
          <div className="mt-2 overflow-hidden rounded-xl border border-canvas-line">
            {rows.map((r, i) => (
              <div key={r.name} className={cn('flex items-center gap-3 bg-white px-3 py-2.5', i > 0 && 'border-t border-canvas-line')}>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-[13px] font-medium text-canvas-fg">{r.name}</p>
                  <p className="truncate text-[11px] text-canvas-muted">{r.reason}</p>
                </div>
                <span className={cn('shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold', STATUS_PILL[r.status])}>
                  {r.status}
                </span>
                {r.status === 'Attention' && (
                  <span className="hidden shrink-0 items-center gap-1 rounded-full border border-canvas-line px-2 py-1 text-[10px] font-medium text-canvas-fg sm:inline-flex">
                    <Wrench className="h-3 w-3 text-accent" /> Fix
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </MockChrome>
  );
}

function MockReviewPanel() {
  const rows = [
    { title: 'Phone number hard to tap', detail: 'Homepage · mobile', status: 'Attention' },
    { title: 'Services not listed clearly', detail: 'Homepage hero', status: 'Attention' },
    { title: 'Missing service-area mention', detail: 'Above the fold', status: 'Attention' },
    { title: 'Valid SSL certificate', detail: 'All public pages', status: 'Good' },
  ];
  return (
    <MockChrome title="Website Growth Review · Findings">
      <div className="bg-white p-4">
        <div className="flex items-center justify-between gap-2 pb-3">
          <div>
            <p className="text-[11px] font-medium uppercase tracking-wider text-canvas-muted">Labeled Sample</p>
            <p className="text-[15px] font-semibold text-canvas-fg">What a review looks like</p>
          </div>
          <Link to="/sample-report" className="text-[12px] font-semibold text-accent hover:underline">
            Open sample →
          </Link>
        </div>
        <div className="overflow-hidden rounded-xl border border-canvas-line">
          {rows.map((r, i) => (
            <div key={r.title} className={cn('flex items-center gap-3 px-3 py-2.5', i > 0 && 'border-t border-canvas-line')}>
              <div className="min-w-0 flex-1">
                <p className="truncate text-[13px] font-medium text-canvas-fg">{r.title}</p>
                <p className="truncate text-[11px] text-canvas-muted">{r.detail}</p>
              </div>
              <span className={cn('rounded-full px-2 py-0.5 text-[10px] font-semibold', STATUS_PILL[r.status])}>{r.status}</span>
            </div>
          ))}
        </div>
      </div>
    </MockChrome>
  );
}

function MockQuotePanel() {
  return (
    <MockChrome title="Quote Requests · Inbox">
      <div className="bg-white p-4">
        <div className="flex items-center justify-between gap-2">
          <div>
            <p className="text-[11px] font-medium uppercase tracking-wider text-canvas-muted">Public page</p>
            <p className="text-[15px] font-semibold text-canvas-fg">app.truetradepath.com/q/joes-plumbing</p>
          </div>
          <span className="rounded-full bg-success-subtle px-2.5 py-1 text-[11px] font-semibold text-success">Published</span>
        </div>
        <div className="mt-3 space-y-2">
          {[
            { name: 'Maria G.', detail: 'Leaky faucet · prefers text · today', value: 'New' },
            { name: 'Tom R.', detail: 'Water heater quote · photos attached', value: 'New' },
          ].map((r) => (
            <div key={r.name} className="flex items-center gap-3 rounded-xl border border-canvas-line bg-[#fbfcfe] px-3 py-2.5">
              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-accent-soft text-[11px] font-semibold text-accent">
                {r.name
                  .split(' ')
                  .map((w) => w[0])
                  .join('')}
              </span>
              <div className="min-w-0 flex-1">
                <p className="truncate text-[13px] font-medium text-canvas-fg">{r.name}</p>
                <p className="truncate text-[11px] text-canvas-muted">{r.detail}</p>
              </div>
              <span className="rounded-full bg-accent px-2.5 py-1 text-[11px] font-medium text-white">{r.value}</span>
            </div>
          ))}
        </div>
      </div>
    </MockChrome>
  );
}

function MockProofPanel() {
  return (
    <MockChrome title="Reviews & Project Proof">
      <div className="grid gap-3 bg-white p-4 sm:grid-cols-2">
        <div className="rounded-xl border border-canvas-line bg-[#fbfcfe] p-3">
          <MessageSquare className="h-4 w-4 text-accent" />
          <p className="mt-2 text-[13px] font-semibold text-canvas-fg">Ask for a review</p>
          <p className="mt-1 text-[12px] text-canvas-muted">Neutral copy you send from your own email or text.</p>
          <span className="mt-3 inline-flex rounded-lg bg-accent px-2.5 py-1.5 text-[11px] font-medium text-white">Copy message</span>
        </div>
        <div className="rounded-xl border border-canvas-line bg-[#fbfcfe] p-3">
          <Image className="h-4 w-4 text-accent" />
          <p className="mt-2 text-[13px] font-semibold text-canvas-fg">Project showcase</p>
          <p className="mt-1 text-[12px] text-canvas-muted">Photos with customer consent — hidden the moment consent is revoked.</p>
          <span className="mt-3 inline-flex items-center gap-1 text-[11px] font-medium text-accent">
            <Phone className="h-3 w-3" /> QR + short link
          </span>
        </div>
      </div>
    </MockChrome>
  );
}

function MockFixPanel() {
  return (
    <MockChrome title="Improvement Kit · Fix drafts">
      <div className="bg-white p-4">
        <p className="text-[11px] font-medium uppercase tracking-wider text-canvas-muted">Ready to paste</p>
        <p className="mt-1 text-[15px] font-semibold text-canvas-fg">Homepage contact section</p>
        <div className="mt-3 rounded-xl border border-canvas-line bg-[#fbfcfe] p-3 font-mono text-[12px] leading-relaxed text-canvas-fg">
          Call or text (555) 123-4567
          <br />
          Serving Tampa Bay · Same-week estimates
          <br />
          Licensed &amp; insured · Free quote request
        </div>
        <div className="mt-3 flex gap-2">
          <span className="rounded-lg bg-accent px-2.5 py-1.5 text-[11px] font-medium text-white">Copy text</span>
          <span className="rounded-lg border border-canvas-line px-2.5 py-1.5 text-[11px] font-medium text-canvas-fg">Download .html</span>
        </div>
      </div>
    </MockChrome>
  );
}

/* ══════════════════════════ Tool marquee ══════════════════════════ */

function ToolMarquee() {
  const tools = ['Your existing website', 'Google Business Profile', 'QR on the truck', 'Text & email', 'Embed on any page', 'No rebuild'];
  const loop = [...tools, ...tools];
  return (
    <section className="border-b border-canvas-line bg-canvas py-10">
      <p className="px-4 text-center text-[13px] font-medium text-canvas-muted">
        Built for the site and channels you already use — we never ask you to rip and replace
      </p>
      <div className="mkt-marquee-mask mt-6 overflow-hidden">
        <div className="mkt-marquee-track gap-12 px-6">
          {loop.map((t, i) => (
            <span key={i} className="whitespace-nowrap text-[17px] font-semibold text-canvas-fg/40">
              {t}
            </span>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ══════════════════════════ Problem ══════════════════════════ */

function ProblemSection() {
  const problems = [
    {
      icon: AlertTriangle,
      title: 'Unclear on mobile',
      desc: 'Phone numbers that aren’t tappable, buried contact forms, and service lists customers can’t find in ten seconds.',
    },
    {
      icon: Search,
      title: 'Guesswork “audits”',
      desc: 'Generic SEO tools invent rankings and traffic. You need evidence of what’s broken on your public pages — and how to fix it.',
    },
    {
      icon: MessageSquare,
      title: 'Hard to get quotes & proof',
      desc: 'Customers won’t fill a clunky form. Reviews go unasked. Project photos sit on your phone instead of building trust.',
    },
  ];
  return (
    <section className="relative px-4 py-20 md:py-28">
      <div className="mx-auto max-w-6xl">
        <SectionHeading
          eyebrow="The leak"
          title={
            <>
              Your website may be quietly
              <br className="hidden md:block" /> costing you jobs
            </>
          }
          desc="Most trade sites look fine on a desktop — then lose customers on the phone. Three places it shows up:"
        />
        <div className="mt-14 grid gap-5 md:grid-cols-3">
          {problems.map(({ icon: Icon, title, desc }) => (
            <div
              key={title}
              className="rounded-2xl border border-canvas-line bg-canvas-raised p-6 transition-all duration-200 hover:border-canvas-fg/15 hover:shadow-[0_16px_40px_-20px_rgba(11,17,32,0.2)]"
            >
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-danger-subtle text-danger">
                <Icon className="h-5 w-5" />
              </div>
              <h3 className="mt-5 text-[17px] font-semibold text-canvas-fg">{title}</h3>
              <p className="mt-2 text-[14px] leading-relaxed text-canvas-muted">{desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ══════════════════════════ How it works ══════════════════════════ */

function HowItWorksSection() {
  return (
    <section id="how" className="relative overflow-hidden border-y border-canvas-line bg-canvas-raised px-4 py-20 md:py-28">
      <div className="mkt-dotgrid pointer-events-none absolute inset-0 opacity-60" />
      <div className="relative mx-auto max-w-6xl">
        <SectionHeading eyebrow="How it works" title="Find · Fix · Win — on the site you already have" />
        <div className="relative mt-16 grid gap-10 md:grid-cols-3 md:gap-8">
          <div className="absolute left-[16%] right-[16%] top-7 hidden h-px bg-gradient-to-r from-transparent via-canvas-line to-transparent md:block" />
          {steps.map(({ icon: Icon, title, desc }) => (
            <div key={title} className="relative text-center">
              <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl border border-canvas-line bg-canvas text-accent shadow-sm">
                <Icon className="h-6 w-6" />
              </div>
              <h3 className="mt-5 text-[18px] font-semibold text-canvas-fg">{title}</h3>
              <p className="mx-auto mt-2 max-w-xs text-[14px] leading-relaxed text-canvas-muted">{desc}</p>
            </div>
          ))}
        </div>
        <div className="mt-14 flex justify-center">
          <Cta to="/signup" variant="outline-light">
            Try it free
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
          </Cta>
        </div>
      </div>
    </section>
  );
}

/* ══════════════════════════ Feature tabs ══════════════════════════ */

type FeatureTab = {
  tab: string;
  icon: ComponentType<{ className?: string }>;
  title: string;
  desc: string;
  Mock: ComponentType;
};

const featureTabs: FeatureTab[] = [
  {
    tab: 'Website review',
    icon: FileSearch,
    title: 'See what’s costing you customers',
    desc: 'A Website Growth Review with Needs attention / Looks good / Could not check — evidence only, no invented traffic numbers.',
    Mock: MockReviewPanel,
  },
  {
    tab: 'Improvements',
    icon: Wrench,
    title: 'Fix it with copy-ready drafts',
    desc: 'Paste-ready text and HTML for contact sections, service pages, and technical SEO — you stay in control of your site.',
    Mock: MockFixPanel,
  },
  {
    tab: 'Quote requests',
    icon: Send,
    title: 'Make it easy to ask for a quote',
    desc: 'A mobile-friendly quote page with an owner inbox. Share via link, QR, embed, or your Google profile.',
    Mock: MockQuotePanel,
  },
  {
    tab: 'Reviews & proof',
    icon: Sparkles,
    title: 'Ask for reviews and show real work',
    desc: 'Honest review messages, consented project photos, QR cards, and proof you can embed — revoke consent and it’s gone everywhere.',
    Mock: MockProofPanel,
  },
];

function FeatureTabs() {
  const [active, setActive] = useState(0);
  const current = featureTabs[active] ?? featureTabs[0];
  const ActiveMock = current.Mock;

  return (
    <section id="features" className="px-4 py-20 md:py-28">
      <div className="mx-auto max-w-6xl">
        <SectionHeading
          eyebrow="The kits"
          title="Four kits. One place."
          desc="Use what you need this week. Nothing locks you out of another kit."
        />

        <div className="mt-14 grid gap-8 lg:grid-cols-[22rem_1fr] lg:gap-12">
          <div className="-mx-4 flex snap-x gap-3 overflow-x-auto px-4 pb-2 lg:mx-0 lg:flex-col lg:overflow-visible lg:px-0 lg:pb-0">
            {featureTabs.map((t, i) => {
              const isActive = i === active;
              return (
                <button
                  key={t.tab}
                  type="button"
                  onClick={() => setActive(i)}
                  className={cn(
                    'w-[78%] shrink-0 snap-start rounded-2xl border p-5 text-left transition-all duration-200 sm:w-[58%] lg:w-full lg:shrink',
                    isActive
                      ? 'border-accent/40 bg-canvas-raised shadow-[0_16px_40px_-24px_rgba(43,84,228,0.4)]'
                      : 'border-transparent hover:bg-canvas-raised/70',
                  )}
                >
                  <div className="flex items-center gap-3">
                    <span
                      className={cn(
                        'flex h-9 w-9 shrink-0 items-center justify-center rounded-xl transition-colors',
                        isActive ? 'bg-accent text-white' : 'bg-accent-soft text-accent',
                      )}
                    >
                      <t.icon className="h-4 w-4" />
                    </span>
                    <h3 className={cn('font-display text-[16px] font-semibold', isActive ? 'text-canvas-fg' : 'text-canvas-muted')}>
                      {t.title}
                    </h3>
                  </div>
                  <p className={cn('mt-3 text-[13.5px] leading-relaxed', isActive ? 'text-canvas-muted' : 'text-canvas-muted/70')}>
                    {t.desc}
                  </p>
                </button>
              );
            })}
          </div>

          <div className="relative min-w-0">
            <div
              className="pointer-events-none absolute -inset-6 -z-10 rounded-[32px]"
              style={{ background: 'radial-gradient(60% 60% at 50% 40%, rgba(43,84,228,0.1), transparent 70%)' }}
            />
            <div key={active} className="mkt-reveal">
              <ActiveMock />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ══════════════════════════ Feature grid ══════════════════════════ */

function FeatureGrid() {
  return (
    <section className="border-t border-canvas-line bg-canvas-raised px-4 py-20 md:py-24">
      <div className="mx-auto max-w-6xl">
        <SectionHeading eyebrow="More in the box" title="Built for how trade businesses actually run" />
        <div className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {gridFeatures.map(({ icon: Icon, title, desc }) => (
            <div
              key={title}
              className="group rounded-2xl border border-canvas-line bg-canvas p-6 transition-all duration-200 hover:border-accent/40 hover:shadow-[0_16px_40px_-22px_rgba(43,84,228,0.28)]"
            >
              <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-accent-soft text-accent transition-colors group-hover:bg-accent group-hover:text-white">
                <Icon className="h-5 w-5" />
              </div>
              <h3 className="mt-4 text-[15.5px] font-semibold text-canvas-fg">{title}</h3>
              <p className="mt-1.5 text-[13.5px] leading-relaxed text-canvas-muted">{desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ══════════════════════════ Metrics band ══════════════════════════ */

function MetricsBand() {
  return (
    <section className="px-4 py-6">
      <div className="mkt-grain relative mx-auto max-w-6xl overflow-hidden rounded-[28px] bg-ink px-6 py-14 md:px-14 md:py-16">
        <div
          className="pointer-events-none absolute inset-0"
          style={{
            background:
              'radial-gradient(50% 80% at 12% 0%, rgba(91,132,255,0.24), transparent 60%), radial-gradient(45% 70% at 100% 100%, rgba(123,92,255,0.18), transparent 60%)',
          }}
        />
        <div className="relative grid items-center gap-10 lg:grid-cols-[1.1fr_1fr]">
          <div>
            <Eyebrow tone="dark">The promise</Eyebrow>
            <h2 className="mt-4 font-display text-[26px] font-semibold leading-[1.12] tracking-[-0.02em] text-ink-fg sm:text-[32px] md:text-[38px]">
              Fix what&apos;s costing you customers this week — not after a rebuild
            </h2>
            <p className="mt-4 max-w-lg text-[15px] leading-relaxed text-ink-muted sm:text-[15.5px]">
              We only read your public pages. You choose what to paste. No fabricated rankings, traffic, or “jobs lost”
              numbers — just clear findings and practical kits.
            </p>
            <div className="mt-8">
              <Cta to="/signup" size="lg">
                Check my website free
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
              </Cta>
            </div>
          </div>
          <div className="grid gap-4 sm:grid-cols-3 lg:grid-cols-1">
            {metrics.map((m) => (
              <div key={m.label} className="rounded-2xl border border-ink-line bg-white/[0.03] p-5 backdrop-blur">
                <p className="font-display text-4xl font-semibold tracking-tight text-accent-bright">{m.value}</p>
                <p className="mt-2 text-[13px] leading-relaxed text-ink-muted">{m.label}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ══════════════════════════ Free while launch (replaces pricing) ══════════════════════════ */

function FreeWhileLaunchSection() {
  const included = [
    'Website Growth Review with evidence',
    'Improvement Kit drafts you can paste',
    'Quote request page + owner inbox',
    'Reviews & Project Proof tools',
    'Share links, QR, and embeds',
    'Export or delete your data anytime',
  ];
  return (
    <section id="pricing" className="px-4 py-20 md:py-28">
      <div className="mx-auto max-w-6xl">
        <SectionHeading
          eyebrow="Pricing"
          title="Free while we launch"
          desc="No credit card. No fake “free forever” claim. Core kits are unlocked so we can learn with real contractors."
        />
        <div className="mx-auto mt-14 max-w-lg">
          <div className="mkt-grain relative overflow-hidden rounded-2xl bg-ink p-8 text-ink-fg shadow-[0_30px_70px_-30px_rgba(43,84,228,0.65)]">
            <div
              className="pointer-events-none absolute inset-0"
              style={{ background: 'radial-gradient(80% 50% at 50% 0%, rgba(91,132,255,0.22), transparent 60%)' }}
            />
            <div className="relative">
              <div className="flex items-center justify-between">
                <h3 className="text-[17px] font-semibold text-white">Contractor Growth Kit</h3>
                <span className="inline-flex items-center gap-1 rounded-full bg-accent px-2.5 py-1 text-[10.5px] font-semibold text-white">
                  <Star className="h-3 w-3 fill-white" /> Launch
                </span>
              </div>
              <p className="mt-1 text-[13px] text-ink-muted">Website check-up and growth toolkit for US trade businesses</p>
              <p className="mt-4 flex items-baseline gap-1">
                <span className="font-display text-[36px] font-semibold tracking-tight text-white">$0</span>
                <span className="text-ink-muted">while we launch</span>
              </p>
              <ul className="mt-5 space-y-2.5">
                {included.map((l) => (
                  <li key={l} className="flex items-start gap-2.5 text-[13px] text-ink-fg">
                    <span className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-accent/25">
                      <Check className="h-2.5 w-2.5 text-accent-bright" strokeWidth={3} />
                    </span>
                    {l}
                  </li>
                ))}
              </ul>
              <Cta to="/signup" size="md" className="mt-6 w-full">
                Get started free
              </Cta>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ══════════════════════════ FAQ ══════════════════════════ */

function FaqSection() {
  const per = Math.ceil(faqs.length / 3);
  const columns = [faqs.slice(0, per), faqs.slice(per, per * 2), faqs.slice(per * 2)];
  return (
    <section className="relative overflow-hidden border-t border-canvas-line bg-canvas px-4 py-20 md:py-28">
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            'radial-gradient(45% 40% at 12% 8%, rgba(43,84,228,0.06), transparent 60%), radial-gradient(40% 40% at 92% 95%, rgba(123,92,255,0.06), transparent 60%)',
        }}
      />
      <div className="relative mx-auto max-w-6xl">
        <div className="mx-auto max-w-2xl text-center">
          <h2 className="font-display text-[28px] font-semibold leading-[1.1] tracking-[-0.025em] text-canvas-fg sm:text-[32px] md:text-[40px]">
            Frequently asked questions
          </h2>
          <p className="mt-4 text-[15px] leading-relaxed text-canvas-muted sm:text-[16px]">
            Can&apos;t find what you&apos;re looking for?{' '}
            <a href={`mailto:${BRAND.supportEmail}`} className="font-medium text-accent hover:text-accent-hover">
              Email us
            </a>{' '}
            and we&apos;ll help.
          </p>
        </div>
        <ul className="mx-auto mt-14 grid max-w-2xl grid-cols-1 gap-8 sm:mt-16 sm:max-w-none sm:grid-cols-2 lg:grid-cols-3">
          {columns.map((col, ci) => (
            <li key={ci}>
              <ul className="flex flex-col gap-8">
                {col.map((f) => (
                  <li key={f.q} className="rounded-2xl border border-canvas-line bg-canvas-raised p-6">
                    <h3 className="font-display text-[15.5px] font-semibold leading-snug text-canvas-fg">{f.q}</h3>
                    <p className="mt-3 text-[14px] leading-relaxed text-canvas-muted">{f.a}</p>
                  </li>
                ))}
              </ul>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

/* ══════════════════════════ Final CTA ══════════════════════════ */

function FinalCta() {
  return (
    <section className="px-4 py-16 md:py-20">
      <div className="mkt-grain relative mx-auto max-w-5xl overflow-hidden rounded-[32px] bg-ink px-6 py-16 text-center md:py-20">
        <div
          className="mkt-glow pointer-events-none absolute inset-0"
          style={{ background: 'radial-gradient(55% 70% at 50% 0%, rgba(91,132,255,0.3), transparent 65%)' }}
        />
        <div className="relative">
          <Eyebrow tone="dark">Get started</Eyebrow>
          <h2 className="mx-auto mt-4 max-w-2xl font-display text-[28px] font-semibold leading-[1.08] tracking-[-0.02em] text-ink-fg sm:text-[36px] md:text-[46px]">
            Check your website this week
          </h2>
          <p className="mx-auto mt-4 max-w-xl text-[15px] leading-relaxed text-ink-muted sm:text-[16px]">
            Paste your URL, see what&apos;s unclear or hard to contact, and fix it on the site you already have.
          </p>
          <div className="mt-9 flex flex-col items-center justify-center gap-3 sm:flex-row">
            <Cta to="/signup" size="lg" className="w-full sm:w-auto">
              Get started free
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
            </Cta>
            <Cta to="/login" variant="outline-dark" size="lg" className="w-full sm:w-auto">
              Sign in
            </Cta>
          </div>
          <p className="mt-5 inline-flex items-center gap-2 text-[13px] text-ink-muted">
            <Clock className="h-3.5 w-3.5" /> No credit card · Free while we launch
          </p>
        </div>
      </div>
    </section>
  );
}

/* ══════════════════════════ Footer ══════════════════════════ */

const LEGAL_LINKS: [string, string][] = [
  ['Privacy', '/privacy'],
  ['Terms', '/terms'],
  ['Security', '/security'],
  ['Cookies', '/cookies'],
  ['Contact', '/contact'],
  ['Remove report', '/remove-report'],
];

function FooterSection() {
  return (
    <footer className="border-t border-canvas-line bg-canvas">
      <div className="mx-auto max-w-6xl px-4 py-14">
        <div className="grid gap-10 md:grid-cols-2 lg:grid-cols-[1.5fr_1fr_1fr_1.3fr]">
          <div>
            <Link to="/" className="flex items-center gap-2.5">
              <Logomark className="h-8 w-8" />
              <span className="text-[15px] font-semibold tracking-tight text-canvas-fg">{BRAND.name}</span>
            </Link>
            <p className="mt-4 max-w-xs text-[13.5px] leading-relaxed text-canvas-muted">{BRAND.tagline}</p>
          </div>

          <FooterCol title="Product">
            <FooterA href="#features">Kits</FooterA>
            <FooterA href="#how">How it works</FooterA>
            <FooterL to="/sample-report">Sample report</FooterL>
            <FooterL to="/how-it-works">Deep dive</FooterL>
          </FooterCol>

          <FooterCol title="Account">
            <FooterL to="/signup">Get started free</FooterL>
            <FooterL to="/login">Sign in</FooterL>
            <FooterL to="/contact">Contact</FooterL>
          </FooterCol>

          <FooterCol title="Get help">
            <a
              href={`mailto:${BRAND.supportEmail}`}
              className="mt-1 inline-flex w-fit items-center gap-1.5 rounded-xl border border-accent/30 bg-accent-soft px-3.5 py-2.5 text-[13.5px] font-semibold text-accent transition-colors hover:border-accent/50 hover:bg-accent-soft/70"
            >
              Email support
              <ArrowRight className="h-3.5 w-3.5" />
            </a>
          </FooterCol>
        </div>
      </div>

      <div className="border-t border-canvas-line">
        <div className="mx-auto flex max-w-6xl flex-col items-center gap-4 px-4 py-6 text-[13px] text-canvas-muted sm:flex-row sm:justify-between">
          <p>{BRAND.copyright}</p>
          <nav className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2">
            {LEGAL_LINKS.map(([label, to]) => (
              <Link key={to} to={to} className="transition-colors hover:text-canvas-fg">
                {label}
              </Link>
            ))}
          </nav>
        </div>
      </div>
    </footer>
  );
}

function FooterCol({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-3">
      <p className="text-[12px] font-semibold uppercase tracking-[0.12em] text-canvas-fg">{title}</p>
      {children}
    </div>
  );
}

function FooterA({ href, children }: { href: string; children: ReactNode }) {
  return (
    <a href={href} className="text-[13.5px] text-canvas-muted transition-colors hover:text-accent">
      {children}
    </a>
  );
}

function FooterL({ to, children }: { to: string; children: ReactNode }) {
  return (
    <Link to={to} className="text-[13.5px] text-canvas-muted transition-colors hover:text-accent">
      {children}
    </Link>
  );
}

function MobileCta() {
  return (
    <div className="fixed inset-x-0 bottom-0 z-30 border-t border-canvas-line bg-canvas/95 p-3 backdrop-blur md:hidden">
      <Cta to="/signup" className="w-full">
        Get started free
      </Cta>
    </div>
  );
}
